import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Table, Button, Modal, Form, Input, Select, DatePicker, Space, message, InputNumber, Divider, List, Avatar, Tag, Upload, Popover, Checkbox, Popconfirm, Collapse } from 'antd';
import { UserOutlined, LinkOutlined, PaperClipOutlined, ArrowLeftOutlined, PlusOutlined, SettingOutlined, FilePdfOutlined, CalendarOutlined, FileSearchOutlined, EditOutlined } from '@ant-design/icons';
import { useAuth } from '../store/auth';
import { useFileViewer } from '../store/FileViewerContext';
import LinkifyText from '../components/LinkifyText';
import TaskChecklist from '../components/TaskChecklist';
import TaskBPM from '../components/TaskBPM';
import SmartMentionsInput from '../components/SmartMentionsInput';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { TasksService } from '../api/services/tasks.service';
import { ProjectsService } from '../api/services/projects.service';
import { UsersService } from '../api/services/users.service';
import { CalendarService } from '../api/services/calendar.service';
import api from '../api/client';
import FilePickerModal from '../components/FilePickerModal';
import GanttChartWrapper from '../components/GanttChartWrapper';
import UniversalFileUploadModal from '../components/UniversalFileUploadModal';
import TaskDetailModal from '../components/TaskDetailModal';
import TaskCreateModal from '../components/TaskCreateModal';

const statusOptions = [
  { value: 'new', label: 'Новая' },
  { value: 'in_progress', label: 'В работе' },
  { value: 'review', label: 'На проверке' },
  { value: 'done', label: 'Готово' },
  { value: 'cancelled', label: 'Отменена' },
];

const priorityOptions = [
  { value: 'low', label: 'Низкий' },
  { value: 'medium', label: 'Средний' },
  { value: 'high', label: 'Высокий' },
  { value: 'critical', label: 'Критический' },
];

const taskTypeOptions = [
  { value: 'land_legal', label: 'Земельно-правовые' },
  { value: 'permissive', label: 'Разрешительные' },
  { value: 'expert', label: 'Экспертные' },
  { value: 'design_engineering', label: 'Проектно-инженерные' },
  { value: 'financial_estimate', label: 'Финансово-сметные' },
  { value: 'control_supervision', label: 'Контрольно-надзорные' },
  { value: 'logistics', label: 'Логистические' },
  { value: 'coordination', label: 'Координационные' },
  { value: 'sales_marketing', label: 'Продажно-маркетинговые' },
  { value: 'cadastral_registration', label: 'Кадастрово-регистрационные' },
  { value: 'warranty', label: 'Гарантийные (постстроительные)' },
  { value: 'judicial_claim', label: 'Судебно-претензионные' },
  { value: 'mobilization', label: 'Мобилизационные (ЧС/форс-мажор)' },
  { value: 'acceptance_transfer', label: 'Приемо-передаточные (УК)' },
  { value: 'organizational_regime', label: 'Организационно-режимные' },
  { value: 'household_administrative', label: 'Хозяйственно-административные (АХО)' },
  { value: 'gr_tasks', label: 'GR-задачи (административное лоббирование)' },
  { value: 'commercial_negotiations', label: 'Коммерческие переговоры с партнёрами' },
];

const participantRoleOptions = [
  { value: 'watcher', label: 'Наблюдатель' },
  { value: 'validator', label: 'Валидатор' },
  { value: 'approver', label: 'Утверждающий' },
  { value: 'curator', label: 'Куратор' },
  { value: 'assignee', label: 'Исполнитель' },
];

const dependencyTypeOptions = [
  { value: 'finish_to_start', label: 'Завершение → Начало (FS)' },
  { value: 'start_to_start', label: 'Начало → Начало (SS)' },
  { value: 'finish_to_finish', label: 'Завершение → Завершение (FF)' },
  { value: 'start_to_finish', label: 'Начало → Завершение (SF)' },
];

export default function ProjectDetailsPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { openFileCard } = useFileViewer();
  const extractFileId = (url) => {
    const match = url?.match(/([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/i);
    return match ? match[1] : null;
  };

  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [projectCommentText, setProjectCommentText] = useState('');
  const [projectUploadModalOpen, setProjectUploadModalOpen] = useState(false);
  const [editProjectModalOpen, setEditProjectModalOpen] = useState(false);

  const [viewMode, setViewMode] = useState('table');
  const [ganttData, setGanttData] = useState([]);
  const [ganttLoading, setGanttLoading] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState([
    'title', 'task_type', 'status', 'priority', 'progress', 'due_date', 'updated_at'
  ]);
  
  const projects = project ? [project] : [];

  // Task Modals
  const [taskDetailModalOpen, setTaskDetailModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [participantsModalOpen, setParticipantsModalOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [parentTask, setParentTask] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm();


  const [participantsForm] = Form.useForm();
  const [editProjectForm] = Form.useForm();

  const handleOpenCreateSubtask = () => {
    setParentTask(selectedTask);
    setTaskDetailModalOpen(false);
    form.resetFields();
    form.setFieldsValue({
      project_id: selectedTask.project_id,
      task_type: selectedTask.task_type,
      initiator_id: user ? user.id : undefined
    });
    setModalOpen(true);
  };

  const fetchProject = async () => {
    setLoading(true);
    try {
      const res = await ProjectsService.getProjectById(projectId);
      setProject(res.data);
    } catch (error) {
      message.error('Ошибка загрузки проекта');
    } finally {
      setLoading(false);
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await TasksService.getTasks({ project_id: projectId });
      setTasks(response.data);
    } catch (error) {
      console.error('Error fetching tasks', error);
    }
  };

  const fetchGantt = async () => {
    setGanttLoading(true);
    try {
      const response = await TasksService.getProjectGantt(projectId);
      setGanttData(response.data);
    } catch (error) {
      console.error('Error fetching gantt', error);
    }
    setGanttLoading(false);
  };

  const fetchUsers = async () => {
    try {
      const response = await UsersService.getUsers();
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users', error);
    }
  };

  const avatarUrl = (avatarId) => avatarId ? `/api/v1/files/${avatarId}/content` : null;

  useEffect(() => {
    if (user && projectId) {
      fetchProject();
      fetchTasks();
      fetchGantt();
      fetchUsers();
    }
  }, [user, projectId]);

  const [exportPdfLoading, setExportPdfLoading] = useState(false);

  const handleExportPDF = async () => {
    setExportPdfLoading(true);
    message.loading({ content: 'Генерация PDF (это может занять время)...', key: 'pdf-gen' });
    await new Promise(r => setTimeout(r, 500));
    try {
      const doc = new jsPDF({ orientation: 'portrait', unit: 'px', format: 'a4' });
      
      const page1 = document.getElementById('pdf-page-1');
      if (page1) {
        const canvas1 = await html2canvas(page1, { scale: 2, useCORS: true });
        const img1 = canvas1.toDataURL('image/jpeg', 0.8);
        const pdfWidth = doc.internal.pageSize.getWidth();
        const pdfHeight = (canvas1.height * pdfWidth) / canvas1.width;
        doc.addImage(img1, 'JPEG', 0, 0, pdfWidth, pdfHeight);
      }

      const sortedGanttData = ganttData ? [...ganttData].sort((a, b) => new Date(a.due_date || 0) - new Date(b.due_date || 0)) : [];
      const ganttChunksCount = Math.max(1, Math.ceil(sortedGanttData.length / 40));

      // Wait until all gantt chunks are fully rendered in DOM
      for (let attempt = 0; attempt < 20; attempt++) {
        const allRendered = Array.from({ length: ganttChunksCount }).every((_, i) => {
          const el = document.getElementById(`pdf-gantt-${i}`);
          return el && (el.querySelector('svg') || el.innerText.includes('Нет задач'));
        });
        if (allRendered) break;
        await new Promise(r => setTimeout(r, 500));
      }

      for (let i = 0; i < ganttChunksCount; i++) {
        const ganttWrapper = document.getElementById(`pdf-gantt-${i}`);
        if (!ganttWrapper) {
          console.warn(`Gantt wrapper pdf-gantt-${i} not found!`);
          continue;
        }
        
        doc.addPage('a3', 'landscape');

        // Find the actual width of the rendered tasks to crop empty space at the end
        try {
          const svgElement = ganttWrapper.querySelector('svg');
          if (svgElement) {
            let maxRight = 0;
            const rects = svgElement.querySelectorAll('rect, text');
            rects.forEach(rect => {
              const x = parseFloat(rect.getAttribute('x') || 0);
              const width = parseFloat(rect.getAttribute('width') || rect.getBoundingClientRect().width || 0);
              // Ignore very wide background rects that span the whole grid
              if (width < 3000 && (x + width > maxRight)) {
                maxRight = x + width;
              }
            });

            if (maxRight > 0) {
              const newWidth = maxRight + 150; // 150px padding to the right for text/arrows
              svgElement.setAttribute('width', newWidth.toString());
              
              const scrollContainer = svgElement.parentElement;
              if (scrollContainer) {
                 scrollContainer.style.width = `${newWidth}px`;
                 scrollContainer.style.overflow = 'hidden';
              }
              
              // Also adjust the parent wrapper if it's flex
              const flexWrapper = scrollContainer?.parentElement;
              if (flexWrapper && flexWrapper.style.display === 'flex') {
                 flexWrapper.style.width = 'max-content';
              }
            }
          }
        } catch (e) {
          console.error("Error cropping gantt", e);
        }

        const ganttHeight = ganttWrapper.offsetHeight || 1000;
        const ganttScale = ganttChunksCount > 10 ? 1 : (ganttHeight > 8000 ? 1 : 1.5);
        const canvas2 = await html2canvas(ganttWrapper, { scale: ganttScale, useCORS: true });
        const img2 = canvas2.toDataURL('image/jpeg', 0.8);
        
        if (img2 !== 'data:,') {
          const a3Width = doc.internal.pageSize.getWidth();
          const a3Height = doc.internal.pageSize.getHeight();
          let finalWidth = a3Width;
          let finalHeight = (canvas2.height * a3Width) / canvas2.width;
          if (finalHeight > a3Height) {
            finalHeight = a3Height;
            finalWidth = (canvas2.width * a3Height) / canvas2.height;
          }
          const xOffset = (a3Width - finalWidth) / 2;
          const yOffset = (a3Height - finalHeight) / 2;
          doc.addImage(img2, 'JPEG', xOffset, yOffset, finalWidth, finalHeight);
        } else {
          console.warn(`Gantt canvas ${i} generated empty data URI due to size limits`);
        }
      }

      doc.addPage('a4', 'portrait');
      const page3 = document.getElementById('pdf-page-3');
      if (page3) {
        const canvas3 = await html2canvas(page3, { scale: 2, useCORS: true });
        const img3 = canvas3.toDataURL('image/jpeg', 0.8);
        const pdfWidth = doc.internal.pageSize.getWidth();
        const pdfHeight = (canvas3.height * pdfWidth) / canvas3.width;
        doc.addImage(img3, 'JPEG', 0, 0, pdfWidth, pdfHeight);
      }

      doc.save(`project_${project?.id}_export.pdf`);
      message.success({ content: 'PDF успешно сгенерирован!', key: 'pdf-gen' });
    } catch (err) {
      console.error('PDF export error:', err);
      message.error({ content: 'Ошибка генерации PDF', key: 'pdf-gen' });
    }
    setExportPdfLoading(false);
  };

  const handleEditProject = async (values) => {
    if (!project) return;
    
    const curators = values.curators || [];
    const managers = values.managers || [];
    const participantsList = values.participants || [];
    
    const formattedParticipants = [
      ...managers.map(id => ({ user_id: id, role: 'manager' })),
      ...curators.map(id => ({ user_id: id, role: 'curator' })),
      ...participantsList.map(id => ({ user_id: id, role: 'participant' }))
    ];

    const payload = {
      end_date: values.end_date?.format('YYYY-MM-DD') || null,
      participants: formattedParticipants,
      comment: values.comment
    };

    try {
      await ProjectsService.updateProject(project.id, payload);
      message.success('Параметры проекта обновлены');
      setEditProjectModalOpen(false);
      editProjectForm.resetFields();
      fetchProject();
    } catch (error) {
      message.error('Ошибка редактирования проекта: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleAddProjectComment = async (uploadData = null) => {
    if (!projectCommentText.trim() && !uploadData) return;
    if (!project) return;
    
    const textToSubmit = projectCommentText;
    setProjectCommentText('');
    
    try {
      const formData = new FormData();
      formData.append('content', textToSubmit || (uploadData ? 'Прикреплен файл' : ''));
      if (uploadData) {
        const files = uploadData.file.getAll('file');
        files.forEach(f => formData.append('file', f));
        // Currently ProjectComments might not store extra metadata in DB, but we pass it anyway
        formData.append('title', uploadData.title);
      }

      await ProjectsService.addCommentWithFile(project.id, formData);
      message.success('Комментарий добавлен');
      if (uploadData) setProjectUploadModalOpen(false);
      fetchProject();
    } catch (error) {
      setProjectCommentText(textToSubmit);
      message.error('Ошибка добавления комментария к проекту');
    }
  };

  const handleViewTaskDetails = async (task) => {
    try {
      const response = await TasksService.getTaskById(task.id);
      setSelectedTask(response.data);
      setTaskDetailModalOpen(true);
    } catch (error) {
      message.error('Не удалось загрузить детали задачи');
      setSelectedTask(task);
      setTaskDetailModalOpen(true);
    }
  };


  async function handleUpdateTask(values) {
    if (!selectedTask) return;
    const payload = {
      ...values,
      start_date: values.start_date?.format('YYYY-MM-DD'),
      due_date: values.due_date?.format('YYYY-MM-DD'),
      crash_date: values.crash_date?.format('YYYY-MM-DD'),
      actual_due_date: values.actual_due_date?.format('YYYY-MM-DD'),
    };
    await TasksService.updateTask(selectedTask.id, payload);
    message.success('Задача обновлена');
    setTaskDetailModalOpen(false);
    fetchTasks();

  ;
  };


  async function handleCreateTask(values) {
    if (!user) {
      message.error('Требуется авторизация');
      navigate('/login');
      return;
    }
    if (creating) return;
    
    setCreating(true);
    
    const payload = {
      ...values,
      start_date: values.start_date?.format('YYYY-MM-DD'),
      due_date: values.due_date?.format('YYYY-MM-DD'),
      crash_date: values.crash_date?.format('YYYY-MM-DD'),
      initiator_id: values.initiator_id || user.id,
      curator_id: values.curator_id,
      parent_id: parentTask ? parentTask.id : undefined,
    };

    try {
      await TasksService.createTask(payload);
      message.success('Задача создана');
      setModalOpen(false);
      setParentTask(null);
      form.resetFields();
      fetchTasks();
      fetchGantt();
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        message.error('Требуется авторизация');
        navigate('/login');
      } else {
        message.error('Ошибка создания задачи: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setCreating(false);
    }
  };

  const handleAddParticipant = async (values) => {
    if (!selectedTask) return;
    await TasksService.addParticipants(selectedTask.id, values);
    message.success('Участник добавлен');
    setParticipantsModalOpen(false);
    participantsForm.resetFields();
    fetchTasks();
  };

  const taskColumns = [
    { 
      title: 'Название', 
      dataIndex: 'title', 
      key: 'title',
      sorter: (a, b) => (a.title || '').localeCompare(b.title || ''),
      render: (text, record) => (
        <Button 
          type="link" 
          onClick={() => handleViewTaskDetails(record)}
          style={{ 
            textDecoration: (record.status === 'done' || record.status === 'cancelled') ? 'line-through' : 'none',
            color: (record.status === 'done' || record.status === 'cancelled') ? 'gray' : undefined 
          }}
        >
          {text}
        </Button>
      )
    },
    {
      title: 'Тип', 
      dataIndex: 'task_type', 
      key: 'task_type',
      sorter: (a, b) => {
        const aLabel = taskTypeOptions.find(opt => opt.value === a.task_type)?.label || a.task_type || '';
        const bLabel = taskTypeOptions.find(opt => opt.value === b.task_type)?.label || b.task_type || '';
        return aLabel.localeCompare(bLabel);
      },
      render: (type) => {
        const option = taskTypeOptions.find(opt => opt.value === type);
        return option ? option.label : type;
      }
    },
    {
      title: 'Статус', 
      dataIndex: 'status', 
      key: 'status',
      sorter: (a, b) => {
        const aLabel = statusOptions.find(opt => opt.value === a.status)?.label || a.status || '';
        const bLabel = statusOptions.find(opt => opt.value === b.status)?.label || b.status || '';
        return aLabel.localeCompare(bLabel);
      },
      render: (s) => (
        <Select size="small" value={s} options={statusOptions} disabled style={{ width: 130 }} />
      ),
    },
    {
      title: 'Приоритет', 
      dataIndex: 'priority', 
      key: 'priority',
      sorter: (a, b) => {
        const aLabel = priorityOptions.find(opt => opt.value === a.priority)?.label || a.priority || '';
        const bLabel = priorityOptions.find(opt => opt.value === b.priority)?.label || b.priority || '';
        return aLabel.localeCompare(bLabel);
      },
      render: (p) => {
        const colors = { low: 'blue', medium: 'orange', high: 'red', critical: 'magenta' };
        return <Tag color={colors[p]}>{priorityOptions.find(opt => opt.value === p)?.label || p}</Tag>;
      },
    },
    { 
      title: 'Прогресс', 
      dataIndex: 'progress', 
      key: 'progress', 
      sorter: (a, b) => (a.progress || 0) - (b.progress || 0),
      render: (v) => `${v}%` 
    },
    { 
      title: 'Срок', 
      dataIndex: 'due_date', 
      key: 'due_date',
      sorter: (a, b) => new Date(a.due_date || 0) - new Date(b.due_date || 0),
      render: (date) => date ? dayjs(date).format('DD.MM.YYYY') : '-'
    },
    {
      title: 'Активность',
      dataIndex: 'updated_at',
      key: 'updated_at',
      sorter: (a, b) => new Date(a.updated_at || 0) - new Date(b.updated_at || 0),
      render: (date) => {
        if (!date) return '-';
        const d = dayjs(date);
        return d.isSame(dayjs(), 'day') ? d.format('HH:mm') : d.format('DD.MM.YYYY HH:mm');
      }
    }
  ];

  const projectTasksTree = useMemo(() => {
    if (!project) return [];
    
    const taskMap = {};
    const roots = [];

    // First pass: map all tasks and initialize children array
    tasks.forEach(task => {
      taskMap[task.id] = { ...task, children: [] };
    });

    // Second pass: attach children to parents
    tasks.forEach(task => {
      if (task.parent_id && taskMap[task.parent_id]) {
        taskMap[task.parent_id].children.push(taskMap[task.id]);
      } else {
        roots.push(taskMap[task.id]);
      }
    });

    // Clean up empty children arrays so AntD doesn't show expand icon for leaves
    const cleanEmptyChildren = (nodes) => {
      nodes.forEach(node => {
        if (node.children.length === 0) {
          delete node.children;
        } else {
          cleanEmptyChildren(node.children);
        }
      });
    };
    
    cleanEmptyChildren(roots);
    
    // В данном случае TasksService.getTasks({ project_id: projectId }) уже отдает 
    // только задачи этого проекта, так что roots - это и есть корневые задачи проекта.
    return roots;
  }, [tasks, project]);

  const filteredTasks = projectTasksTree;
  const filteredColumns = taskColumns.filter(col => visibleColumns.includes(col.key));

  if (loading || !project) {
    return <div style={{ textAlign: 'center', padding: '40px' }}>Загрузка данных проекта...</div>;
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/projects')}>Назад</Button>
            <Button icon={<FilePdfOutlined />} onClick={handleExportPDF} loading={exportPdfLoading}>PDF</Button>
          </Space>
          <Space>
          {(user?.role_level >= 5 || user?.username === 'khaminov') && (
            <Popconfirm
              title="Вы уверены, что хотите удалить этот проект?"
              onConfirm={async () => {
                try {
                  await ProjectsService.deleteProject(project.id);
                  message.success('Проект успешно удален');
                  navigate('/projects');
                } catch (error) {
                  message.error('Ошибка удаления проекта');
                }
              }}
              okText="Удалить"
              cancelText="Отмена"
            >
              <Button danger>Удалить проект</Button>
            </Popconfirm>
          )}
          {project && (project.owner_id === user?.id || project.participants?.some(p => p.user_id === user?.id && (p.role === 'manager' || p.role === 'curator')) || user?.role_level >= 4) && (
            <Button
              type="primary"
              onClick={() => {
                editProjectForm.setFieldsValue({
                  end_date: project.end_date ? dayjs(project.end_date) : null,
                  managers: project.participants?.filter(p => p.role === 'manager').map(p => p.user_id) || [],
                  curators: project.participants?.filter(p => p.role === 'curator').map(p => p.user_id) || [],
                  participants: project.participants?.filter(p => p.role === 'participant').map(p => p.user_id) || [],
                  comment: '',
                });
                setEditProjectModalOpen(true);
              }}
            >
              Редактировать проект
            </Button>
          )}
        </Space>
        </div>
      </div>
      
      <h2>{project.name}</h2>
      
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px', background: '#fff', padding: '24px', borderRadius: '8px' }}>
        <div>
          <h4>Основная информация</h4>
          <p><strong>Описание:</strong> {project.description ? <LinkifyText text={project.description} style={{ display: 'inline' }} /> : 'Нет описания'}</p>
          <p><strong>Бюджет:</strong> {project.budget ? `${project.budget} ₽` : 'Не указан'}</p>
          <p><strong>Руководители:</strong> {project.participants?.filter(p => p.role === 'manager').map(p => p.full_name || users.find(u => String(u.id) === String(p.user_id))?.full_name || `ID: ${p.user_id}`).join(', ') || 'Нет'}</p>
          <p><strong>Кураторы:</strong> {project.participants?.filter(p => p.role === 'curator').map(p => p.full_name || users.find(u => String(u.id) === String(p.user_id))?.full_name || `ID: ${p.user_id}`).join(', ') || 'Только владелец'}</p>
          <p><strong>Сотрудники:</strong> {project.participants?.filter(p => p.role === 'participant').map(p => p.full_name || users.find(u => String(u.id) === String(p.user_id))?.full_name || `ID: ${p.user_id}`).join(', ') || 'Нет'}</p>
        </div>
        <div>
          <h4>Даты</h4>
          <p><strong>Дата начала:</strong> {project.start_date ? dayjs(project.start_date).format('DD.MM.YYYY') : 'Не указана'}</p>
          <p><strong>Дата окончания:</strong> {project.end_date ? dayjs(project.end_date).format('DD.MM.YYYY') : 'Не указана'}</p>
          <p><strong>Создан:</strong> {project.created_at ? dayjs(project.created_at).format('DD.MM.YYYY HH:mm') : 'Не указано'}</p>
        </div>
      </div>

      <Divider />
      
      <Collapse
        items={[
          {
            key: 'bpm',
            label: <b>Маршруты согласования</b>,
            children: (
              <TaskBPM project={project} onUpdate={fetchProject} hideStartButton />
            )
          }
        ]}
        style={{ marginBottom: 24 }}
      />
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Space>
          <h3>Задачи проекта</h3>
          <Popover
            content={
              <Checkbox.Group
                style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
                value={visibleColumns}
                onChange={setVisibleColumns}
              >
                {taskColumns.map(col => (
                  <Checkbox key={col.key} value={col.key}>
                    {col.title}
                  </Checkbox>
                ))}
              </Checkbox.Group>
            }
            title="Выберите колонки"
            trigger="click"
            placement="bottomLeft"
          >
            <Button icon={<SettingOutlined />}>Колонки</Button>
          </Popover>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => {
            form.resetFields();
            if (user) {
              form.setFieldsValue({ initiator_id: user.id, project_id: parseInt(projectId) });
            }
            setModalOpen(true);
          }}>Новая задача</Button>
        </Space>
        <Select 
          value={viewMode} 
          onChange={setViewMode} 
          options={[
            { value: 'table', label: 'Таблица' },
            { value: 'gantt', label: 'Диаграмма Ганта' }
          ]} 
          style={{ width: 160 }}
        />
      </div>

      {viewMode === 'table' ? (
        <Table 
          dataSource={filteredTasks} 
          columns={filteredColumns} 
          rowKey="id" 
          scroll={{ x: 'max-content' }}
        />
      ) : (
        <div style={{ position: 'relative' }}>
          {ganttLoading && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(255,255,255,0.7)', zIndex: 10, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>Загрузка Ганта...</div>}
          <GanttChartWrapper 
            ganttData={ganttData} 
            onTaskClick={(task) => {
              const fullTask = tasks.find(t => t.id === parseInt(task.id));
              if (fullTask) handleViewTaskDetails(fullTask);
            }} 
          />
        </div>
      )}

      <Divider />
      
      <div style={{ marginTop: '24px', background: '#fff', padding: '24px', borderRadius: '8px' }}>
        <h4>Комментарии к проекту</h4>
        <List
          itemLayout="horizontal"
          dataSource={project.comments || []}
          locale={{ emptyText: 'Нет комментариев' }}
          renderItem={(comment) => {
            const author = users.find(u => String(u.id) === String(comment.user_id));
            return (
              <List.Item>
                <List.Item.Meta
                  avatar={
                    <Avatar 
                      src={author?.avatar_file_id ? avatarUrl(author.avatar_file_id) : null} 
                      icon={!author?.avatar_file_id && <UserOutlined />} 
                    />
                  }
                  title={comment.author_full_name || (author ? author.full_name || author.email : null) || `ID: ${comment.user_id}`}
                  description={
                    <>
                      <LinkifyText text={comment.content} />
                      {comment.attached_files && comment.attached_files.length > 0 ? (
                        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {comment.attached_files.map((f, i) => (
                            <a key={i} onClick={() => {
                              const id = f.file_id || extractFileId(f.file_url);
                              if (id) {
                                openFileCard(id, { project_id: project.id });
                              } else {
                                window.open(f.file_url, '_blank');
                              }
                            }} style={{ 
                              cursor: 'pointer', 
                              color: '#1890ff',
                              border: comment.file_approval_status === 'active' ? '2px solid #faad14' : comment.file_approval_status === 'cancelled' ? '2px solid #f5222d' : comment.file_approval_status === 'completed' ? '2px solid #52c41a' : 'none',
                              backgroundColor: comment.file_approval_status === 'active' ? 'rgba(250, 173, 20, 0.05)' : comment.file_approval_status === 'completed' ? 'rgba(82, 196, 26, 0.05)' : comment.file_approval_status === 'cancelled' ? 'rgba(245, 34, 45, 0.05)' : 'transparent',
                              padding: comment.file_approval_status ? '2px 4px' : '0',
                              borderRadius: comment.file_approval_status ? '4px' : '0',
                              display: 'inline-block'
                            }}>
                              <PaperClipOutlined /> {f.file_name || 'Прикрепленный файл'}
                            </a>
                          ))}
                        </div>
                      ) : comment.file_url ? (
                        <div style={{ marginTop: 8 }}>
                          <a onClick={() => {
                            const id = extractFileId(comment.file_url);
                            if (id) {
                              openFileCard(id, { project_id: project.id });
                            } else {
                              window.open(comment.file_url, '_blank');
                            }
                          }} style={{ 
                            cursor: 'pointer', 
                            color: '#1890ff',
                            border: comment.file_approval_status === 'active' ? '2px solid #faad14' : comment.file_approval_status === 'cancelled' ? '2px solid #f5222d' : comment.file_approval_status === 'completed' ? '2px solid #52c41a' : 'none',
                            backgroundColor: comment.file_approval_status === 'active' ? 'rgba(250, 173, 20, 0.05)' : comment.file_approval_status === 'completed' ? 'rgba(82, 196, 26, 0.05)' : comment.file_approval_status === 'cancelled' ? 'rgba(245, 34, 45, 0.05)' : 'transparent',
                            padding: comment.file_approval_status ? '2px 4px' : '0',
                            borderRadius: comment.file_approval_status ? '4px' : '0',
                            display: 'inline-block'
                          }}>
                            <PaperClipOutlined /> {comment.file_name || 'Прикрепленный файл'}
                          </a>
                        </div>
                      ) : null}
                      <span style={{ fontSize: '0.8em', color: 'gray' }}>
                        {dayjs(comment.created_at).format('DD.MM.YYYY HH:mm')}
                      </span>
                    </>
                  }
                />
              </List.Item>
            );
          }}
        />
        
        <div style={{ marginTop: 16 }}>
          <SmartMentionsInput 
            rows={3} 
            value={projectCommentText} 
            onChange={(val) => setProjectCommentText(val)} 
            placeholder="Добавить комментарий к проекту..."
            onSubmit={() => handleAddProjectComment(null)}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
            <Button icon={<PaperClipOutlined />} onClick={() => setProjectUploadModalOpen(true)}>Прикрепить файл</Button>
            <Button type="primary" onClick={() => handleAddProjectComment(null)}>
              Отправить
            </Button>
          </div>
        </div>
      </div>
      
      <UniversalFileUploadModal
        open={projectUploadModalOpen}
        onCancel={() => setProjectUploadModalOpen(false)}
        onUpload={(data) => handleAddProjectComment(data)}
        defaultProjectId={project?.id}
      />

      
      <TaskCreateModal
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          setParentTask(null);
          form.resetFields();
        }}
        form={form}
        onFinish={handleCreateTask}
        users={users}
        projects={projects}
        tasks={tasks}
        parentTask={parentTask}
        taskTypeOptions={taskTypeOptions}
        priorityOptions={priorityOptions}
        user={user}
        confirmLoading={creating}
      />

      <TaskDetailModal 
        open={taskDetailModalOpen}
        onClose={() => setTaskDetailModalOpen(false)}
        selectedTask={selectedTask}
        setSelectedTask={setSelectedTask}
        users={users}
        projects={projects}
        tasks={tasks}
        onUpdate={fetchTasks}
        onOpenCreateSubtask={handleOpenCreateSubtask}
      />
</div>
  );
}
